const PREVIEW_LIMIT = 10

export interface BrowserPrintLabel {
  url: string
  caption?: string
}

export interface BrowserPrintPayload {
  title: string
  labels: BrowserPrintLabel[]
  markTargets: { target_type: string; sequence_number: number }[]
  force?: boolean
  expectedCount?: number
}

export function previewLabels(labels: BrowserPrintLabel[]): BrowserPrintLabel[] {
  return labels.slice(0, PREVIEW_LIMIT)
}

async function loadImageBytes(url: string): Promise<{ bytes: Uint8Array; kind: 'png' | 'jpeg' }> {
  if (url.startsWith('data:')) {
    const match = /^data:(image\/(?:png|jpeg|jpg));base64,(.+)$/i.exec(url)
    if (!match) throw new Error('Неподдерживаемый формат изображения')
    const kind = match[1].includes('png') ? 'png' : 'jpeg'
    const binary = atob(match[2])
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return { bytes, kind }
  }

  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) throw new Error(`Не удалось загрузить этикетку (${res.status})`)
  const bytes = new Uint8Array(await res.arrayBuffer())
  const ct = (res.headers.get('content-type') ?? url).toLowerCase()
  const kind: 'png' | 'jpeg' = ct.includes('png') ? 'png' : 'jpeg'
  return { bytes, kind }
}

function triggerDownload(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.click()
  window.setTimeout(() => URL.revokeObjectURL(href), 0)
}

export async function downloadLabelsPdf(
  labels: BrowserPrintLabel[],
  filename: string,
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  if (!labels.length) throw new Error('Нет этикеток для PDF')

  const { PDFDocument } = await import('pdf-lib')
  const pdf = await PDFDocument.create()
  const total = labels.length

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i]
    const { bytes, kind } = await loadImageBytes(label.url)
    const image = kind === 'png' ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes)
    const { width, height } = image.scale(1)
    const page = pdf.addPage([width, height])
    page.drawImage(image, { x: 0, y: 0, width, height })
    onProgress?.(i + 1, total)
  }

  onProgress?.(total, total)

  const pdfBytes = await pdf.save()
  triggerDownload(new Blob([Uint8Array.from(pdfBytes)], { type: 'application/pdf' }), filename.endsWith('.pdf') ? filename : `${filename}.pdf`)
}

export function printLabelsViaBrowser(urls: string[], copies = 1): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!urls.length) {
      reject(new Error('Нет этикеток для печати'))
      return
    }

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) {
      document.body.removeChild(iframe)
      reject(new Error('Не удалось открыть окно печати'))
      return
    }

    const pages: string[] = []
    for (const url of urls) {
      const n = Math.max(1, copies)
      for (let i = 0; i < n; i++) {
        pages.push(`<div class="page"><img src="${escapeAttr(url)}" alt="" /></div>`)
      }
    }

    doc.open()
    doc.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>TrueMark print</title>
<style>
  @page { margin: 0; }
  html, body { margin: 0; padding: 0; }
  .page { page-break-after: always; display: flex; align-items: center; justify-content: center; min-height: 100vh; }
  .page:last-child { page-break-after: auto; }
  img { max-width: 100%; max-height: 100vh; object-fit: contain; }
</style></head><body>${pages.join('')}</body></html>`)
    doc.close()

    const cleanup = () => {
      window.removeEventListener('afterprint', onAfterPrint)
      if (iframe.parentNode) document.body.removeChild(iframe)
    }

    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      cleanup()
      resolve()
    }

    const onAfterPrint = () => finish()

    const waitImages = () => {
      const imgs = Array.from(doc.images)
      if (imgs.length === 0) {
        triggerPrint()
        return
      }
      let pending = imgs.length
      const done = () => {
        pending -= 1
        if (pending <= 0) triggerPrint()
      }
      for (const img of imgs) {
        if (img.complete) done()
        else {
          img.onload = done
          img.onerror = done
        }
      }
    }

    const triggerPrint = () => {
      window.addEventListener('afterprint', onAfterPrint)
      try {
        win.focus()
        win.print()
      } catch (err) {
        cleanup()
        reject(err instanceof Error ? err : new Error('Печать не удалась'))
        return
      }
      window.setTimeout(finish, 60_000)
    }

    waitImages()
  })
}

function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
}

export { PREVIEW_LIMIT }
